// dataInvalidation.js — lightweight cross-module invalidation bus.
//
// Problem this solves: when a mutation in one module changes data that another
// module is displaying (e.g. creating a task in /tasks that should appear in
// the inbox sidebar's tasks list for the same contact), there's no signal for
// the other module to refetch. Users see stale data until they manually
// refresh.
//
// Pattern: any mutation site broadcasts which entity-type changed and (when
// known) the specific id. Subscribers compare against what they're displaying
// and refetch if relevant.
//
// This is an interim layer until we adopt React Query (which has built-in
// invalidateQueries). Once that lands, this module becomes a thin shim or is
// deleted entirely. See `inbox_reliability_plan.md` for the migration path.
//
// API:
//   invalidate('tasks', { contactId: '...' })   // narrow broadcast
//   invalidate('contacts')                       // broad broadcast
//   onInvalidate('tasks', (payload) => {...})    // subscribe to one entity
//   onInvalidate('*', (entity, payload) => {...}) // subscribe to all
//
// Returns an unsubscribe function from onInvalidate.

const ENTITY_TYPES = new Set([
  'contacts', 'companies', 'deals', 'projects', 'properties',
  'opportunities', 'tasks', 'client_records', 'call_log',
  'email_intelligence', 'saved_emails',
])

const target = typeof window !== 'undefined'
  ? (window.__chatroneInvalidate ||= new EventTarget())
  : new EventTarget()

export function invalidate(entityType, payload = {}) {
  if (!entityType) return
  if (entityType !== '*' && !ENTITY_TYPES.has(entityType)) {
    // Not fatal — log so typos surface, but still broadcast.
    console.warn(`[invalidate] unknown entity type "${entityType}"`)
  }
  const detail = { entityType, ...payload, at: Date.now() }
  target.dispatchEvent(new CustomEvent(entityType, { detail }))
  target.dispatchEvent(new CustomEvent('*', { detail }))
}

export function onInvalidate(entityType, listener) {
  if (!entityType || typeof listener !== 'function') return () => {}
  const handler = (e) => {
    try {
      if (entityType === '*') listener(e.detail.entityType, e.detail)
      else listener(e.detail)
    } catch (err) {
      console.warn('[invalidate] listener threw:', err?.message)
    }
  }
  target.addEventListener(entityType, handler)
  return () => target.removeEventListener(entityType, handler)
}
